import { Alert, Button, EmptyState, FieldBlock, Input, LoadingState, useToast } from '@orbind/ui'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useEffect, useMemo, useState } from 'react'
import { FormRow, FormScreen, FormSection } from '../../components/FormScreen.tsx'
import { api } from '../../lib/api.ts'
import './areas.css'

/**
 * Cadastro de Áreas (SPEC-20260814-2334) — referencia do padrao "sem modal":
 * a lista vive na area de trabalho e Incluir/Editar trocam o conteudo pelo
 * FormScreen (padrao Conta Azul: secoes recolhiveis, campos em colunas na
 * linha, rodape com Cancelar/Salvar e dirty check). A exclusao e confirmada
 * INLINE na linha, sem dialogo.
 */

type AreaList = Awaited<ReturnType<Awaited<ReturnType<typeof api.catalog.areas.$get>>['json']>>
type AreaRow = AreaList['areas'][number]

/** "Atendimento ao Empresa" -> "ATENDIMENTO_AO_CLIENTE". */
function normalizarCodigo(texto: string) {
  return texto
    .normalize('NFD')
    .replace(/\p{Diacritic}/gu, '')
    .replace(/[^A-Za-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '')
    .toUpperCase()
}

type FormArea = { namePt: string; nameEn: string; code: string; isActive: boolean }

export function AreasSection({ criarSinal = 0 }: { criarSinal?: number }) {
  const toast = useToast()
  const qc = useQueryClient()

  const lista = useQuery({
    queryKey: ['catalog-areas'],
    queryFn: async () => (await api.catalog.areas.$get()).json(),
  })

  const [busca, setBusca] = useState('')
  /** null = lista; { id: null } = incluindo; { id } = editando — tudo no main. */
  const [editando, setEditando] = useState<{ id: string | null } | null>(null)
  const [form, setForm] = useState<FormArea>({ namePt: '', nameEn: '', code: '', isActive: true })
  const [confirmandoExclusao, setConfirmandoExclusao] = useState<string | null>(null)

  useEffect(() => {
    if (criarSinal > 0) abrir(null)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [criarSinal])

  const invalidar = () => {
    void qc.invalidateQueries({ queryKey: ['catalog-areas'] })
    void qc.invalidateQueries({ queryKey: ['bootstrap'] })
    void qc.invalidateQueries({ queryKey: ['i18n'] })
  }

  const aoFalhar = (erro: unknown) =>
    toast.push({
      tone: 'danger',
      message: erro instanceof Error && erro.message ? erro.message : 'Não foi possível concluir. Tente novamente.',
    })

  const criar = useMutation({
    mutationFn: async (f: FormArea) =>
      (
        await api.catalog.areas.$post({
          json: {
            code: normalizarCodigo(f.code || f.namePt),
            name: { 'pt-BR': f.namePt.trim(), ...(f.nameEn.trim() ? { 'en-US': f.nameEn.trim() } : {}) },
            isActive: f.isActive,
          },
        })
      ).json(),
    onSuccess: () => {
      invalidar()
      setEditando(null)
      toast.push({ tone: 'success', message: 'Área criada.' })
    },
    onError: aoFalhar,
  })

  const atualizar = useMutation({
    mutationFn: async ({ id, f }: { id: string; f: FormArea }) =>
      (
        await api.catalog.areas[':id'].$patch({
          json: {
            code: normalizarCodigo(f.code || f.namePt),
            name: { 'pt-BR': f.namePt.trim(), ...(f.nameEn.trim() ? { 'en-US': f.nameEn.trim() } : {}) },
            isActive: f.isActive,
          },
          param: { id },
        })
      ).json(),
    onSuccess: () => {
      invalidar()
      setEditando(null)
      toast.push({ tone: 'success', message: 'Área atualizada.' })
    },
    onError: aoFalhar,
  })

  const alternarAtivo = useMutation({
    mutationFn: async ({ id, isActive }: { id: string; isActive: boolean }) =>
      (await api.catalog.areas[':id'].active.$post({ param: { id }, json: { isActive } })).json(),
    onSuccess: invalidar,
    onError: aoFalhar,
  })

  const excluir = useMutation({
    mutationFn: async (id: string) => (await api.catalog.areas[':id'].$delete({ param: { id } })).json(),
    onSuccess: () => {
      invalidar()
      setConfirmandoExclusao(null)
      toast.push({ tone: 'success', message: 'Área excluída.' })
    },
    onError: (erro) => {
      setConfirmandoExclusao(null)
      aoFalhar(erro)
    },
  })

  const areas = lista.data?.areas ?? []
  const emEdicao = editando?.id ? areas.find((a) => a.id === editando.id) : undefined

  const filtradas = useMemo(() => {
    const termo = busca.trim().toLowerCase()
    if (termo === '') return areas
    return areas.filter((a) => `${a.name['pt-BR'] ?? ''} ${a.code}`.toLowerCase().includes(termo))
  }, [areas, busca])

  const abrir = (alvo: AreaRow | null) => {
    setForm(
      alvo
        ? { namePt: alvo.name['pt-BR'] ?? '', nameEn: alvo.name['en-US'] ?? '', code: alvo.code, isActive: alvo.isActive }
        : { namePt: '', nameEn: '', code: '', isActive: true },
    )
    setEditando({ id: alvo?.id ?? null })
  }

  /** Dirty check (preferência do usuário): compara com o registro carregado. */
  const alterado =
    editando?.id == null
      ? form.namePt.trim().length >= 2
      : emEdicao !== undefined &&
        (form.namePt.trim() !== (emEdicao.name['pt-BR'] ?? '') ||
          form.nameEn.trim() !== (emEdicao.name['en-US'] ?? '') ||
          normalizarCodigo(form.code || form.namePt) !== emEdicao.code ||
          form.isActive !== emEdicao.isActive)

  // ------------------------------------------------------- formulário ---

  if (editando !== null) {
    const siglaDerivada = normalizarCodigo(form.code || form.namePt)
    const siglaAutomatica = form.code.trim() === '' && form.namePt.trim() !== ''
    const salvarValido = alterado && form.namePt.trim().length >= 2 && !criar.isPending && !atualizar.isPending

    return (
      <FormScreen
        title={editando.id ? 'Editar área' : 'Nova área'}
        subtitle={editando.id ? (emEdicao?.name['pt-BR'] ?? emEdicao?.code) : undefined}
        onClose={() => setEditando(null)}
        dirty={alterado}
        dirtyHint={editando.id ? 'Tudo salvo' : 'Preencha o nome para salvar'}
        saveLabel={editando.id ? 'Salvar' : 'Criar área'}
        saveDisabled={!salvarValido}
        saving={criar.isPending || atualizar.isPending}
        // Exclusão no rodapé, à esquerda (padrão 2026-08-30).
        onDelete={
          emEdicao
            ? () => {
                const nome = emEdicao.name['pt-BR'] ?? emEdicao.code
                if (!confirm(`Excluir a área “${nome}”?`)) return
                excluir.mutate(emEdicao.id, { onSuccess: () => setEditando(null) })
              }
            : undefined
        }
        deleteLabel="Excluir área"
        deleteDisabled={(emEdicao?.usageCount ?? 0) > 0}
        deleteHint={(emEdicao?.usageCount ?? 0) > 0 ? 'Área em uso: desative-a para tirá-la das opções' : undefined}
        onSubmit={() => {
          if (!salvarValido) return
          if (editando.id) atualizar.mutate({ id: editando.id, f: form })
          else criar.mutate(form)
        }}
      >
        <FormSection title="Informações da área">
          <FormRow cols="2fr 2fr 1fr">
            <FieldBlock label="Nome da área" required hint="Como aparece nas telas em português.">
              {({ id }) => (
                <Input
                  id={id}
                  autoFocus
                  value={form.namePt}
                  placeholder="Ex.: Engenharia de Produto"
                  onChange={(e) => setForm((f) => ({ ...f, namePt: e.target.value }))}
                />
              )}
            </FieldBlock>

            <FieldBlock label="Name (en-US)" hint="Opcional — exibido para quem usa a plataforma em inglês.">
              {({ id }) => (
                <Input
                  id={id}
                  value={form.nameEn}
                  placeholder="Ex.: Product Engineering"
                  onChange={(e) => setForm((f) => ({ ...f, nameEn: e.target.value }))}
                />
              )}
            </FieldBlock>

            <FieldBlock
              label="Sigla"
              hint={
                siglaAutomatica
                  ? `Gerada automaticamente do nome: ${siglaDerivada}. Digite para personalizar.`
                  : 'Identificador técnico, único no workspace.'
              }
            >
              {({ id }) => (
                <Input
                  id={id}
                  value={form.code}
                  placeholder={siglaDerivada || 'Ex.: ENG'}
                  onChange={(e) => setForm((f) => ({ ...f, code: e.target.value }))}
                  onBlur={() => setForm((f) => ({ ...f, code: f.code ? normalizarCodigo(f.code) : f.code }))}
                />
              )}
            </FieldBlock>
          </FormRow>

          <div className="areaform__status">
            <span className="areaform__status-text">
              <strong>{form.isActive ? 'Área ativa' : 'Área inativa'}</strong>
              <small>
                {form.isActive
                  ? 'Aparece nas opções de cadastro de pessoas e demandas.'
                  : 'Some das opções, mas os registros que já a usam continuam intactos.'}
              </small>
            </span>
            <button
              type="button"
              role="switch"
              aria-checked={form.isActive}
              aria-label="Área ativa"
              className={`adm-switch${form.isActive ? ' adm-switch--on' : ''}`}
              onClick={() => setForm((f) => ({ ...f, isActive: !f.isActive }))}
            >
              <span className="adm-switch__knob" aria-hidden />
            </button>
          </div>

          {emEdicao ? (
            <p className="areaform__usage">
              <i className="fas fa-link" aria-hidden />{' '}
              {emEdicao.usageCount > 0
                ? `Em uso por ${emEdicao.usageCount} ${emEdicao.usageCount === 1 ? 'registro' : 'registros'}.`
                : 'Ainda sem uso.'}
            </p>
          ) : null}
        </FormSection>
      </FormScreen>
    )
  }

  // ------------------------------------------------------------ lista ---

  const cabecalho = (
    <div className="adm-head">
      <div>
        <h1 className="adm-head__title">Áreas</h1>
        <p className="adm-head__desc">Áreas da empresa neste workspace — organizam pessoas e demandas.</p>
      </div>
      <Button variant="primary" icon="fas fa-plus" onClick={() => abrir(null)}>
        Incluir
      </Button>
    </div>
  )

  if (lista.isPending) return (<>{cabecalho}<LoadingState variant="list" label="Carregando as áreas" /></>)
  if (lista.isError) {
    return (
      <>
        {cabecalho}
        <div className="adm-placeholder">
          <Alert tone="danger" title="Não foi possível carregar as áreas"
            action={<Button variant="secondary" icon="fas fa-rotate-right" onClick={() => void lista.refetch()}>Tentar de novo</Button>}>
            Verifique a conexão e tente novamente.
          </Alert>
        </div>
      </>
    )
  }

  return (
    <>
      {cabecalho}

      <div className="adm-toolbar">
        <div className="adm-toolbar__search">
          <i className="fas fa-search" aria-hidden />
          <input type="search" placeholder="Filtrar por nome ou sigla…" aria-label="Filtrar áreas"
            value={busca} onChange={(e) => setBusca(e.target.value)} />
        </div>
        <span className="adm-toolbar__count">
          {filtradas.length} de {areas.length} {areas.length === 1 ? 'registro' : 'registros'}
        </span>
      </div>

      {filtradas.length === 0 ? (
        <div className="adm-placeholder">
          <EmptyState icon="fas fa-sitemap"
            title={areas.length === 0 ? 'Nenhuma área ainda' : 'Nenhuma área com esse filtro'}
            description={areas.length === 0
              ? 'Crie a primeira área — ex.: Produto, Engenharia, Atendimento — para organizar pessoas e demandas.'
              : `Nada casa com “${busca}”.`}
            action={areas.length === 0
              ? (<Button variant="primary" icon="fas fa-plus" onClick={() => abrir(null)}>Nova área</Button>)
              : (<Button variant="secondary" icon="fas fa-times" onClick={() => setBusca('')}>Limpar o filtro</Button>)} />
        </div>
      ) : (
        <div className="adm-list">
          {filtradas.map((row) => {
            const confirmando = confirmandoExclusao === row.id
            return (
              <div key={row.id} className={`adm-catrow${row.isActive ? '' : ' adm-catrow--off'}`}>
                <span className="adm-catrow__icon"><i className="fas fa-sitemap" aria-hidden /></span>
                <span className="adm-catrow__main">
                  <span className="adm-catrow__name">{row.name['pt-BR'] ?? row.code}</span>
                  <span className="adm-catrow__sub">
                    Sigla {row.code}
                    {row.usageCount > 0 ? ` · em uso por ${row.usageCount} ${row.usageCount === 1 ? 'registro' : 'registros'}` : ' · sem uso'}
                  </span>
                </span>

                {confirmando ? (
                  // confirmacao INLINE — sem modal, como o usuario prefere
                  <span style={{ display: 'inline-flex', gap: 6, alignItems: 'center' }}>
                    <span style={{ fontSize: 12.5, color: 'var(--ob-danger)' }}>Excluir “{row.name['pt-BR'] ?? row.code}”?</span>
                    <Button variant="danger" loading={excluir.isPending} onClick={() => excluir.mutate(row.id)}>
                      Confirmar
                    </Button>
                    <Button variant="ghost" onClick={() => setConfirmandoExclusao(null)}>
                      Cancelar
                    </Button>
                  </span>
                ) : (
                  <>
                    <span className="adm-switchwrap">
                      <span className="adm-switchwrap__label">{row.isActive ? 'Ativa' : 'Inativa'}</span>
                      <button type="button" role="switch" aria-checked={row.isActive}
                        aria-label={`${row.name['pt-BR'] ?? row.code} está ativa`}
                        title={row.isActive ? 'Ativa — clique para desativar' : 'Inativa — clique para ativar'}
                        className={`adm-switch${row.isActive ? ' adm-switch--on' : ''}`}
                        onClick={() => alternarAtivo.mutate({ id: row.id, isActive: !row.isActive })}>
                        <span className="adm-switch__knob" aria-hidden />
                      </button>
                    </span>
                    <button type="button" className="adm-iconbtn" aria-label={`Editar ${row.name['pt-BR'] ?? row.code}`}
                      title="Editar" onClick={() => abrir(row)}>
                      <i className="fas fa-pen" aria-hidden />
                    </button>
                    <button type="button" className="adm-iconbtn adm-iconbtn--danger" disabled={row.usageCount > 0}
                      aria-label={row.usageCount > 0 ? `${row.code} está em uso e não pode ser excluída` : `Excluir ${row.code}`}
                      title={row.usageCount > 0 ? 'Área em uso: desative-a para tirá-la das opções' : 'Excluir'}
                      onClick={() => setConfirmandoExclusao(row.id)}>
                      <i className="fas fa-trash-alt" aria-hidden />
                    </button>
                  </>
                )}
              </div>
            )
          })}
        </div>
      )}
    </>
  )
}
